/**
 * Custom icon set — ONE TRUE source. Previously inlined SVG-by-SVG in
 * every component that needed an icon. Extracted to keep the visual
 * language consistent across customer-pwa / dashboard / agent-pwa /
 * marketing.
 *
 * Each icon takes an optional `size` prop (default 16). Stroke colour
 * follows `currentColor` so the call site sets the colour via CSS / inline
 * style. Lightweight — these are tiny 16×16 vectors; no library dependency.
 *
 * Per [[feedback-premium-custom-ui]]: stock icon libraries' instantly-
 * recognisable look is banned. These are bespoke to Kilat's visual
 * language (thicker 1.4 stroke, rounded joins, simple geometry).
 */

interface IconProps {
  readonly size?: number;
  readonly className?: string;
  readonly style?: React.CSSProperties;
}

export function GlobeIcon({ size = 16, className, style }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden
      className={className}
      style={style}
    >
      <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.4" />
      <ellipse cx="8" cy="8" rx="2.5" ry="6" stroke="currentColor" strokeWidth="1.2" />
      <path d="M2 8h12" stroke="currentColor" strokeWidth="1.2" />
    </svg>
  );
}

export function ShieldIcon({ size = 16, className, style }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden
      className={className}
      style={style}
    >
      <path
        d="M8 1.5l5.5 2v4.2c0 3.4-2.4 6.5-5.5 7.3-3.1-.8-5.5-3.9-5.5-7.3V3.5L8 1.5z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      <path
        d="M5.8 8l1.7 1.7L10.3 7"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function BoltIcon({ size = 16, className, style }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden
      className={className}
      style={style}
    >
      <path
        d="M8.7 1.5L3 9h4l-.7 5.5L12 7H8l.7-5.5z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function LockIcon({ size = 16, className, style }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden
      className={className}
      style={style}
    >
      <rect x="3" y="7" width="10" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
      <path
        d="M5 7V5a3 3 0 0 1 6 0v2"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function MailIcon({ size = 16, className, style }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden
      className={className}
      style={style}
    >
      <rect x="2" y="3.5" width="12" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
      <path
        d="M2.5 4.5l5.5 4.5 5.5-4.5"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function BuildingIcon({ size = 16, className, style }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden
      className={className}
      style={style}
    >
      <rect
        x="2.5"
        y="2.5"
        width="11"
        height="11"
        rx="1.5"
        stroke="currentColor"
        strokeWidth="1.4"
      />
      <rect x="5" y="5" width="2" height="2" rx="0.3" fill="currentColor" />
      <rect x="9" y="5" width="2" height="2" rx="0.3" fill="currentColor" />
      <rect x="5" y="9" width="2" height="2" rx="0.3" fill="currentColor" />
      <rect x="9" y="9" width="2" height="2" rx="0.3" fill="currentColor" />
    </svg>
  );
}

export function UserIcon({ size = 16, className, style }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden
      className={className}
      style={style}
    >
      <circle cx="8" cy="6" r="2.5" stroke="currentColor" strokeWidth="1.4" />
      <path
        d="M3 13.5c.7-2.4 2.7-3.5 5-3.5s4.3 1.1 5 3.5"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function StarIcon({
  size = 16,
  className,
  style,
  filled = false,
}: IconProps & {
  /**
   * Solid rather than outline.
   *
   * A rating needs both: filled stars for the score and outlines for the
   * remainder. Without this, showing a rating meant the `★`/`☆` text glyphs,
   * which render in whatever the reader's emoji or system font decides and sit
   * beside a custom icon set that agrees on everything else.
   */
  readonly filled?: boolean;
}) {
  const d = 'M8 1.6l1.9 3.9 4.3.6-3.1 3 .7 4.3L8 11.4 4.3 13.4l.7-4.3-3.1-3 4.3-.6L8 1.6z';
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden
      className={className}
      style={style}
    >
      <path
        d={d}
        fill={filled ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * A whole-star rating row, drawn rather than typed.
 *
 * `aria-hidden` on the stars with the score carried by the caller's own text —
 * a screen reader should hear "4.5 out of 5 from 12 reviews", not five icons.
 */
export function StarRating({
  rating,
  size = 14,
  max = 5,
}: {
  readonly rating: number;
  readonly size?: number;
  readonly max?: number;
}) {
  const whole = Math.round(rating);
  return (
    <span aria-hidden style={{ display: 'inline-flex', gap: 1, verticalAlign: 'text-bottom' }}>
      {Array.from({ length: max }, (_, i) => (
        <StarIcon key={i} size={size} filled={i < whole} />
      ))}
    </span>
  );
}

export function TagIcon({ size = 16, className, style }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden
      className={className}
      style={style}
    >
      <path d="M2 8l6-6h5v5l-6 6z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
      <circle cx="10" cy="6" r="1" fill="currentColor" />
    </svg>
  );
}

export function CarIcon({ size = 16, className, style }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden
      className={className}
      style={style}
    >
      <path
        d="M3 10V8.4l1.1-2.6A1.3 1.3 0 0 1 5.3 5h5.4a1.3 1.3 0 0 1 1.2.8L13 8.4V10"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      <path d="M2.4 10h11.2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <circle cx="5.2" cy="11.1" r="1.15" stroke="currentColor" strokeWidth="1.4" />
      <circle cx="10.8" cy="11.1" r="1.15" stroke="currentColor" strokeWidth="1.4" />
    </svg>
  );
}

export function PackageIcon({ size = 16, className, style }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden
      className={className}
      style={style}
    >
      <path
        d="M8 2l5.5 3v6L8 14l-5.5-3V5L8 2z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      <path d="M2.5 5L8 8l5.5-3" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
      <path d="M8 8v6" stroke="currentColor" strokeWidth="1.4" />
    </svg>
  );
}

export function ReceiptIcon({ size = 16, className, style }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden
      className={className}
      style={style}
    >
      <path
        d="M4 2.5h8v11l-1.3-.9-1.35.9-1.35-.9-1.35.9L5.3 12.6 4 13.5z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      <path
        d="M6.1 6h3.8M6.1 8.4h3.8"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function MedalIcon({ size = 16, className, style }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden
      className={className}
      style={style}
    >
      <path
        d="M5.6 2.5l1.7 3.3M10.4 2.5L8.7 5.8"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
      <circle cx="8" cy="9.6" r="3.4" stroke="currentColor" strokeWidth="1.4" />
      <circle cx="8" cy="9.6" r="1.1" fill="currentColor" />
    </svg>
  );
}

export function BellIcon({ size = 16, className, style }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden
      className={className}
      style={style}
    >
      <path
        d="M8 2.3a3.3 3.3 0 0 0-3.3 3.3c0 3-1.1 4-1.1 4h8.8s-1.1-1-1.1-4A3.3 3.3 0 0 0 8 2.3z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      <path
        d="M6.7 11.8a1.4 1.4 0 0 0 2.6 0"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function InfoIcon({ size = 16, className, style }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden
      className={className}
      style={style}
    >
      <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.4" />
      <path d="M8 7.3v3.4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <circle cx="8" cy="5.2" r="0.75" fill="currentColor" />
    </svg>
  );
}

export function GiftIcon({ size = 16, className, style }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden
      className={className}
      style={style}
    >
      <rect x="3" y="7" width="10" height="6.5" rx="1" stroke="currentColor" strokeWidth="1.4" />
      <path d="M2.4 7h11.2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <path d="M8 7v6.5" stroke="currentColor" strokeWidth="1.4" />
      <path
        d="M8 7C7.3 4.9 4.6 5.1 5 6.4 5.3 7.2 8 7 8 7z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      <path
        d="M8 7C8.7 4.9 11.4 5.1 11 6.4 10.7 7.2 8 7 8 7z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Mapping name → component so callers can do `<Icon name="building" />` if
 *  they need dynamic dispatch (e.g., PremiumField's `icon` prop). */
export const ICON_BY_NAME = {
  globe: GlobeIcon,
  shield: ShieldIcon,
  bolt: BoltIcon,
  lock: LockIcon,
  mail: MailIcon,
  building: BuildingIcon,
  user: UserIcon,
  tag: TagIcon,
  star: StarIcon,
  car: CarIcon,
  package: PackageIcon,
  receipt: ReceiptIcon,
  medal: MedalIcon,
  bell: BellIcon,
  info: InfoIcon,
  gift: GiftIcon,
} as const;

export type IconName = keyof typeof ICON_BY_NAME;

/**
 * Appearance icons for the three-state theme control.
 *
 * The auto glyph is a circle split down the middle — half outline, half
 * filled — rather than the usual half-moon-in-a-sun mashup. It reads as "both,
 * decided elsewhere" at 14px, which is what `system` actually means, and it
 * stays legible next to the sun and moon instead of competing with them.
 */
export function SunIcon({ size = 16, className, style }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden
      className={className}
      style={style}
    >
      <circle cx="8" cy="8" r="3.1" stroke="currentColor" strokeWidth="1.4" />
      <path
        d="M8 1.6v1.6M8 12.8v1.6M1.6 8h1.6M12.8 8h1.6M3.5 3.5l1.1 1.1M11.4 11.4l1.1 1.1M12.5 3.5l-1.1 1.1M4.6 11.4l-1.1 1.1"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function MoonIcon({ size = 16, className, style }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden
      className={className}
      style={style}
    >
      <path
        d="M13 9.6A5.6 5.6 0 0 1 6.4 3a5.6 5.6 0 1 0 6.6 6.6Z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function ThemeAutoIcon({ size = 16, className, style }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden
      className={className}
      style={style}
    >
      <circle cx="8" cy="8" r="5.6" stroke="currentColor" strokeWidth="1.4" />
      <path d="M8 2.4a5.6 5.6 0 0 1 0 11.2Z" fill="currentColor" />
    </svg>
  );
}

/**
 * A house, for the primary "home" destination in a tab bar.
 *
 * Same 16×16 grid, 1.4 stroke and rounded joins as the rest of the set — the
 * point of a bespoke set is that a new icon reads as one of the family rather
 * than as a paste from a library.
 */
export function HomeIcon({ size = 16, className, style }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden
      className={className}
      style={style}
    >
      <path
        d="M2.5 7 8 2.5 13.5 7v6a1 1 0 0 1-1 1h-9a1 1 0 0 1-1-1V7Z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      <path d="M6.5 14V9.5h3V14" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
    </svg>
  );
}

/** A map pin, for geographic destinations. */
export function PinIcon({ size = 16, className, style }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden
      className={className}
      style={style}
    >
      <path
        d="M8 14.5s5-4.2 5-7.6A5 5 0 0 0 3 6.9c0 3.4 5 7.6 5 7.6Z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      <circle cx="8" cy="6.8" r="1.8" stroke="currentColor" strokeWidth="1.3" />
    </svg>
  );
}
